import { History, Location } from 'history';

import { IFilterDefinition, OptionType } from '@evinced-private/ui-common';

import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import { Logger } from 'src/common/services/Logger';

const FILTER_TYPE_TEXT = 'text';
const FILTER_TYPE_SELECT = 'select';

const serializeOptionValues = (options: OptionType[]): string => {
	return options
		.map((opt) => encodeURIComponent(String(opt?.value ?? opt)))
		.filter(Boolean)
		.join(',');
};

const parseCommaSeparatedValues = (value: string): string[] => {
	return value
		.split(',')
		.map((v) => decodeURIComponent(v.trim()))
		.filter(Boolean);
};

const findMatchingOption = (
	paramValue: string,
	availableOptions: OptionType[]
): OptionType | undefined => {
	const trimmedValue = paramValue.trim();
	return (
		availableOptions.find((opt) => String(opt.value).trim() === trimmedValue) ||
		availableOptions.find((opt) => String(opt.label || '').trim() === trimmedValue)
	);
};

const buildUrlPath = (pathname: string, searchParams: URLSearchParams): string => {
	const queryString = searchParams.toString();
	return `${pathname}${queryString ? `?${queryString}` : ''}`;
};

export const filtersToQueryParams = (filters: IFilterOptions): URLSearchParams => {
	const params = new URLSearchParams();

	Object.entries(filters).forEach(([key, value]) => {
		if (!value) {
			return;
		}

		if (typeof value === 'string' && value.trim()) {
			params.set(key, value.trim());
			return;
		}

		if (Array.isArray(value) && value.length > 0) {
			const serialized = serializeOptionValues(value);
			if (serialized) {
				params.set(key, serialized);
			}
		}
	});

	return params;
};

export const queryParamsToFilters = (
	searchParams: URLSearchParams,
	filterDefinitions: IFilterDefinition[],
	filterOptions: Record<string, OptionType[]>
): IFilterOptions => {
	const filters: IFilterOptions = {};
	const filterDefMap = new Map(filterDefinitions.map((def) => [def.id, def]));
	const unmatchedParams: string[] = [];

	searchParams.forEach((value, key) => {
		const filterDef = filterDefMap.get(key);
		if (!filterDef) {
			// Skip non-filter query params (like 'unique', 'redirectTo', etc.)
			return;
		}

		if (filterDef.filterType === FILTER_TYPE_TEXT) {
			const trimmed = value.trim();
			if (trimmed) {
				filters[key] = trimmed;
			}
			return;
		}

		if (filterDef.filterType === FILTER_TYPE_SELECT) {
			const availableOptions = filterOptions[key];
			if (!availableOptions?.length) {
				Logger.warn(
					`Query param "${key}" found but no options available yet. Filter will be skipped.`
				);
				return;
			}

			const paramValues = parseCommaSeparatedValues(value);
			if (!paramValues.length) {
				return;
			}

			const matchedOptions = paramValues
				.map((paramValue) => findMatchingOption(paramValue, availableOptions))
				.filter((opt): opt is OptionType => opt !== undefined);

			const unmatchedValues = paramValues.filter(
				(paramValue) => !findMatchingOption(paramValue, availableOptions)
			);

			if (unmatchedValues.length > 0) {
				unmatchedParams.push(`${key}=${unmatchedValues.join(', ')}`);
			}

			if (matchedOptions.length > 0) {
				filters[key] = matchedOptions;
			}
		}
	});

	if (unmatchedParams.length > 0) {
		Logger.warn(
			'Some filter values from URL query parameters could not be matched to available options:',
			unmatchedParams.join('; ')
		);
	}

	return filters;
};

export const updateUrlWithFilters = (
	history: History,
	location: Location,
	filters: IFilterOptions,
	filterDefinitions: IFilterDefinition[]
): void => {
	const params = filtersToQueryParams(filters);
	const filterKeys = new Set(filterDefinitions.map((def) => def.id));

	// Preserve existing non-filter query params
	new URLSearchParams(location.search).forEach((value, key) => {
		if (!filterKeys.has(key)) {
			params.set(key, value);
		}
	});

	const newPath = buildUrlPath(location.pathname, params);
	const currentPath = `${location.pathname}${location.search}`;
	if (currentPath !== newPath) {
		history.replace(newPath);
	}
};
